/**
 * views/PilotInterface.tsx — /dashboard/flight
 * ──────────────────────────────────────────────
 * Pilot field interface.  Redesigned in M9 to support manager-assigned
 * missions with allocated aisle scopes.
 *
 * Layout (when missions exist)
 * ──────────────────────────────────────────────
 *  ┌─ Header ─────────────────────────────────────────────────────┐
 *  │ Logo · Pilot Flight Interface · WS status · Pilot name       │
 *  └──────────────────────────────────────────────────────────────┘
 *  ┌─ My Missions briefing panel ─────────────────────────────────┐
 *  │ Card per assigned mission: title, aisles, dates, [Select]    │
 *  └──────────────────────────────────────────────────────────────┘
 *  ┌─ Upload zone ─────────────────────────────────────────────────┐
 *  │ Active mission banner (if selected) · Drop files here         │
 *  └───────────────────────────────────────────────────────────────┘
 *  ┌─ Live Scan Feed ──────────────────────────────────────────────┐
 *  │ Events with out-of-scope warnings                             │
 *  └───────────────────────────────────────────────────────────────┘
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUploadQueue } from '@/hooks/useUploadQueue';
import { apiClient } from '@/api/client';
import { InfiniumMark } from '@/components/ui/InfiniumLogo';
import { Progress } from '@/components/ui/progress';
import type { Mission, WsEvent } from '@/types';

const PILOT_MISSION_KEY = 'infinium_pilot_mission_id';

// ── Folder reader — async recursive traversal ─────────────────────────────────

async function readDirectoryEntry(dir: FileSystemDirectoryEntry): Promise<File[]> {
  const files: File[] = [];
  const reader = dir.createReader();
  while (true) {
    const entries: FileSystemEntry[] = await new Promise((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    if (entries.length === 0) break;
    for (const entry of entries) {
      if (entry.isFile) {
        const file = await new Promise<File>((resolve, reject) => {
          (entry as FileSystemFileEntry).file(resolve, reject);
        });
        files.push(file);
      } else if (entry.isDirectory) {
        files.push(...await readDirectoryEntry(entry as FileSystemDirectoryEntry));
      }
    }
  }
  return files;
}

async function extractDroppedFiles(transfer: DataTransfer): Promise<File[]> {
  const files: File[] = [];
  if (transfer.items) {
    const tasks: Promise<File[]>[] = [];
    for (const item of Array.from(transfer.items)) {
      if (item.kind !== 'file') continue;
      const entry = item.webkitGetAsEntry();
      if (!entry) continue;
      if (entry.isDirectory) {
        tasks.push(readDirectoryEntry(entry as FileSystemDirectoryEntry));
      } else {
        const f = item.getAsFile();
        if (f) files.push(f);
      }
    }
    const nested = await Promise.all(tasks);
    for (const batch of nested) files.push(...batch);
  } else {
    files.push(...Array.from(transfer.files));
  }
  return files;
}

// ── Format helpers ────────────────────────────────────────────────────────────

function fmtBytes(bytes: number): string {
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

// ── Live scan event type ──────────────────────────────────────────────────────

interface ScanEvent {
  id: string;
  ts: string;
  event: string;
  location_code?: string | null;
  flag?: string;
  canonical_filename?: string;
  out_of_scope?: boolean;
}

// ── Mission briefing panel ────────────────────────────────────────────────────

function MissionCard({
  mission,
  isActive,
  onSelect,
}: {
  mission: Mission;
  isActive: boolean;
  onSelect: () => void;
}) {
  const navigate = useNavigate();
  const [reportLoading, setReportLoading] = useState(false);

  async function handleProcessReport() {
    setReportLoading(true);
    try {
      const token = sessionStorage.getItem('infinium_access_token') ?? '';
      const reconcileRes = await fetch(`/api/missions/${mission.id}/reconcile`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!reconcileRes.ok) {
        alert(`Reconciliation failed (${reconcileRes.status}).`);
        return;
      }
      const { records_written } = await reconcileRes.json() as { records_written: number };
      if (records_written === 0) {
        alert('No assigned photos to report on yet.');
        return;
      }
      navigate(`/dashboard/inventory?mission=${mission.id}`);
    } catch {
      alert('Could not generate report — check your connection.');
    } finally {
      setReportLoading(false);
    }
  }

  const statusColour: Record<string, string> = {
    PENDING:   'ms-status--pending',
    ACTIVE:    'ms-status--active',
    COMPLETED: 'ms-status--done',
    CANCELLED: 'ms-status--cancelled',
  };

  return (
    <div className={`ms-card${isActive ? ' ms-card--selected' : ''}`}>
      <div className="ms-card-header">
        <span className={`ms-status-chip ${statusColour[mission.status] ?? ''}`}>
          {mission.status}
        </span>
        <span className="ms-card-title">{mission.title ?? `Mission ${mission.id.slice(0, 8)}`}</span>
      </div>

      {/* Warehouse */}
      {mission.warehouse_name && (
        <div className="ms-card-row">
          <span className="ms-card-lbl">Warehouse</span>
          <span className="ms-card-val">{mission.warehouse_name}</span>
        </div>
      )}

      {/* Allocated aisles */}
      <div className="ms-card-row">
        <span className="ms-card-lbl">Aisles</span>
        <div className="ms-aisle-chips">
          {mission.allocated_aisles.length > 0
            ? [...mission.allocated_aisles].sort().map(a => (
                <span key={a} className="ms-aisle-chip">{a}</span>
              ))
            : <span className="ms-card-val ms-card-val--dim">All aisles (no restriction)</span>
          }
        </div>
      </div>

      {/* Upload statistics */}
      <div className="ms-card-row" style={{ alignItems: 'flex-start' }}>
        <span className="ms-card-lbl" style={{ paddingTop: '2px' }}>Photos</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
          <span className="ms-card-val">
            <strong>{mission.photos_uploaded}</strong>
            <span style={{ color: '#888' }}> uploaded</span>
          </span>
          <span className="ms-card-val" style={{ color: '#16a34a' }}>
            <strong>{mission.photos_assigned}</strong>
            <span style={{ color: '#888' }}> assigned</span>
            {mission.location_count > 0 && (
              <span style={{ color: '#aaa', fontSize: '11px', marginLeft: '4px' }}>
                ({mission.location_count} locations total)
              </span>
            )}
          </span>
          {(() => {
            const unassigned = mission.photos_uploaded - mission.photos_assigned - mission.deferred_count;
            return unassigned > 0 ? (
              <span className="ms-card-val" style={{ color: '#dc2626' }}>
                <strong>{unassigned}</strong>
                <span style={{ color: '#888' }}> unassigned</span>
              </span>
            ) : null;
          })()}
          {mission.deferred_count > 0 && (
            <span className="ms-card-val" style={{ color: '#d97706' }}>
              <strong>{mission.deferred_count}</strong>
              <span style={{ color: '#888' }}> deferred</span>
            </span>
          )}
        </div>
      </div>

      {/* Time window */}
      {(mission.scheduled_start || mission.scheduled_end) && (
        <div className="ms-card-row">
          <span className="ms-card-lbl">Window</span>
          <span className="ms-card-val">
            {fmtDate(mission.scheduled_start)} → {fmtDate(mission.scheduled_end)}
          </span>
        </div>
      )}

      {/* Notes */}
      {mission.notes && (
        <div className="ms-card-row">
          <span className="ms-card-lbl">Notes</span>
          <span className="ms-card-val ms-card-val--notes">{mission.notes}</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
        <button
          className={`btn ms-select-btn ${isActive ? 'btn--ghost' : 'btn--primary'}`}
          onClick={onSelect}
          style={{ flex: 1, minWidth: '120px' }}
        >
          {isActive ? '✓ Active mission' : 'Select mission →'}
        </button>
        <Link
          to={`/dashboard/triage/${mission.id}`}
          className="btn btn--ghost"
          style={{ flex: 1, minWidth: '80px', textAlign: 'center', textDecoration: 'none' }}
          title="Open exception triage for this mission"
        >
          🔍 Triage
        </Link>
        <button
          className="btn btn--ghost"
          style={{ flex: 1, minWidth: '120px' }}
          title="Reconcile assigned photos then download inventory report as Excel"
          disabled={reportLoading}
          onClick={handleProcessReport}
        >
          {reportLoading ? '⏳ Processing…' : '📊 Process Report'}
        </button>
      </div>
    </div>
  );
}

function MissionBriefing({
  missions,
  activeMission,
  onSelect,
}: {
  missions: Mission[];
  activeMission: Mission | null;
  onSelect: (m: Mission) => void;
}) {
  if (missions.length === 0) return null;

  const active = missions.filter(m => m.status !== 'COMPLETED');
  const past   = missions.filter(m => m.status === 'COMPLETED');

  return (
    <section className="ms-briefing">
      <div className="ms-briefing-header">
        <span className="ms-briefing-icon">📋</span>
        <div>
          <h2 className="ms-briefing-title">My Missions</h2>
          <p className="ms-briefing-sub">Select a mission before uploading your SD card</p>
        </div>
      </div>
      <div className="ms-card-list">
        {active.map(m => (
          <MissionCard
            key={m.id}
            mission={m}
            isActive={activeMission?.id === m.id}
            onSelect={() => onSelect(m)}
          />
        ))}
      </div>

      {past.length > 0 && (
        <details style={{ marginTop: '16px' }}>
          <summary style={{ cursor: 'pointer', fontSize: '13px', color: '#666', padding: '4px 0' }}>
            📁 Past missions ({past.length}) — click to view triage
          </summary>
          <div className="ms-card-list" style={{ marginTop: '8px', opacity: 0.8 }}>
            {past.map(m => (
              <MissionCard
                key={m.id}
                mission={m}
                isActive={false}
                onSelect={() => onSelect(m)}
              />
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PilotInterface() {
  const { user, signOut } = useAuth();
  const navigate          = useNavigate();
  const wsRef             = useRef<WebSocket | null>(null);
  const sessionId         = useRef(`session_${Date.now()}`);

  const [connected,   setConnected]   = useState(false);
  const [events,      setEvents]      = useState<ScanEvent[]>([]);
  const [isDragOver,  setIsDragOver]  = useState(false);

  // ── Missions ──────────────────────────────────────────────────────────────
  const [missions,       setMissions]       = useState<Mission[]>([]);
  const [activeMission,  setActiveMission]  = useState<Mission | null>(null);
  const [missionsLoaded, setMissionsLoaded] = useState(false);

  // ── Aisle selection (Step-6) — pilot declares which aisle they just flew ──
  const [selectedAisle, setSelectedAisle] = useState<string | null>(null);

  useEffect(() => {
    const preselected = sessionStorage.getItem(PILOT_MISSION_KEY);
    if (preselected) sessionStorage.removeItem(PILOT_MISSION_KEY);

    apiClient.get<Mission[]>('/api/missions/mine')
      .then(data => {
        setMissions(data);
        if (preselected) {
          const m = data.find(d => d.id === preselected);
          if (m) { setActiveMission(m); return; }
        }
        // Auto-select the single active mission, if there is exactly one
        if (data.length === 1) setActiveMission(data[0]);
        else {
          const active = data.find(m => m.status === 'ACTIVE');
          if (active) setActiveMission(active);
        }
      })
      .catch(() => { /* no missions or network error — silent */ })
      .finally(() => setMissionsLoaded(true));
  }, []);

  const warehouseId = activeMission?.warehouse_id ?? user?.warehouse_id ?? '';

  const { items, doneCount, failedCount, isRunning, missionId, addFiles, reset, retryFailed } =
    useUploadQueue(sessionId.current, warehouseId, activeMission?.id, activeMission?.upload_max_width);

  // Refresh mission photo counts after a batch finishes so the card stays current.
  const prevIsRunningRef = useRef(false);
  useEffect(() => {
    if (prevIsRunningRef.current && !isRunning && doneCount > 0) {
      apiClient.get<Mission[]>('/api/missions/mine')
        .then(data => setMissions(data))
        .catch(() => {});
    }
    prevIsRunningRef.current = isRunning;
  }, [isRunning, doneCount]);

  const totalCount    = items.length;
  const pendingCount  = totalCount - doneCount - failedCount;
  const currentItem   = items.find(i => i.status === 'uploading');
  // batchComplete used only for conditional LocationRangeCheck rendering (removed in M9 redesign)
  // const batchComplete = totalCount > 0 && !isRunning && pendingCount === 0;

  // ── WebSocket ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${window.location.host}/ws/pilot/${sessionId.current}`);
    wsRef.current = ws;
    ws.onopen  = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onmessage = (msg) => {
      try {
        const data = JSON.parse(msg.data) as WsEvent;
        if (data.event === 'PING') return;
        setEvents(prev => [
          {
            id: crypto.randomUUID(),
            ts: new Date().toLocaleTimeString(),
            event: data.event,
            location_code: data.data?.location_code as string | null,
            flag: data.data?.flag as string,
            canonical_filename: data.data?.canonical_filename as string,
            out_of_scope: data.data?.out_of_scope as boolean | undefined,
          },
          ...prev.slice(0, 49),
        ]);
      } catch { /* ignore malformed frames */ }
    };
    return () => ws.close();
  }, []);

  // ── Drag-and-drop ─────────────────────────────────────────────────────────

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (!warehouseId) {
        alert('Your account has no warehouse assigned. Contact an administrator.');
        return;
      }
      if (missions.length > 0 && !activeMission) {
        alert('Please select a mission before uploading.');
        return;
      }
      const allocatedAisles = activeMission?.allocated_aisles ?? [];
      if (allocatedAisles.length > 0 && !selectedAisle) {
        alert('Please select which aisle you just flew before uploading photos.');
        return;
      }
      const files = await extractDroppedFiles(e.dataTransfer);
      addFiles(files, selectedAisle ?? undefined);
    },
    [warehouseId, missions.length, activeMission, selectedAisle, addFiles],
  );

  const hasBatch = totalCount > 0;

  // Out-of-scope count from live events this session
  const outOfScopeCount = events.filter(e => e.out_of_scope).length;

  return (
    <div className="pilot-page">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <header className="pilot-header">
        <div className="pilot-header-left">
          <InfiniumMark variant="dark" height={34} />
          <h1 className="pilot-header-title">Pilot Flight Interface</h1>
          <span className={`ws-indicator ${connected ? 'ws-indicator--on' : 'ws-indicator--off'}`}>
            {connected ? '● Live' : '○ Connecting…'}
          </span>
        </div>
        <div className="pilot-header-right">
          <button className="btn btn--ghost btn--sm" onClick={() => navigate('/dashboard/missions')}>
            ← Missions
          </button>
          {activeMission && (
            <button
              className="btn btn--ghost btn--sm"
              onClick={() =>
                navigate(
                  `/dashboard/triage/${activeMission.id}${
                    selectedAisle ? `?aisle=${encodeURIComponent(selectedAisle)}` : ''
                  }`,
                )
              }
              title={selectedAisle ? `View triage for aisle ${selectedAisle}` : 'View triage for this mission'}
            >
              🔍 Triage{selectedAisle ? ` · ${selectedAisle}` : ''}
            </button>
          )}
          <span className="pilot-user">{user?.name || user?.email}</span>
          <button className="btn btn--ghost btn--sm" onClick={signOut}>Sign out</button>
        </div>
      </header>

      <div className="pilot-body">

        {/* ── Mission Briefing ─────────────────────────────────────────────── */}
        {missionsLoaded && (
          <MissionBriefing
            missions={missions}
            activeMission={activeMission}
            onSelect={m => { setActiveMission(m); setSelectedAisle(null); reset(); }}
          />
        )}

        {/* No missions assigned — informational only, not a blocker */}
        {missionsLoaded && missions.length === 0 && (
          <div className="ms-no-missions">
            <span className="ms-no-missions-icon">📭</span>
            <p>No missions assigned to you yet. You can still upload — images will be grouped into an auto-created mission.</p>
          </div>
        )}

        {/* ── Active mission scope banner ──────────────────────────────────── */}
        {activeMission && (
          <div className="ms-active-banner">
            <div className="ms-active-banner-left">
              <span className="ms-active-icon">🎯</span>
              <div>
                <span className="ms-active-label">Active Mission</span>
                <span className="ms-active-title">
                  {activeMission.title ?? `Mission ${activeMission.id.slice(0, 8)}`}
                </span>
              </div>
            </div>
            <div className="ms-active-banner-right">
              {activeMission.allocated_aisles.length > 0 && (
                <div className="ms-active-aisles">
                  <span className="ms-active-aisles-lbl">Allocated aisles:</span>
                  {[...activeMission.allocated_aisles].sort().map(a => (
                    <span key={a} className="ms-aisle-chip ms-aisle-chip--active">{a}</span>
                  ))}
                </div>
              )}
              {outOfScopeCount > 0 && (
                <span className="ms-out-of-scope-warn">
                  ⚠ {outOfScopeCount} out-of-scope scan{outOfScopeCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Aisle selector (Step-6) ─────────────────────────────────────── */}
        {activeMission && activeMission.allocated_aisles.length > 0 && (
          <div className="ms-aisle-selector">
            <div className="ms-aisle-selector-header">
              <span className="ms-aisle-selector-lbl">Which aisle did you just fly?</span>
              {selectedAisle && (
                <span className="ms-aisle-selector-chosen">
                  Aisle <strong>{selectedAisle}</strong> selected
                </span>
              )}
            </div>
            <div className="ms-aisle-selector-grid">
              {[...activeMission.allocated_aisles].sort().map(aisle => (
                <button
                  key={aisle}
                  type="button"
                  className={`ms-aisle-toggle${selectedAisle === aisle ? ' ms-aisle-toggle--active' : ''}`}
                  onClick={() => setSelectedAisle(prev => prev === aisle ? null : aisle)}
                >
                  {aisle}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Drop zone ─────────────────────────────────────────────────── */}
        <div
          className={`upload-zone${isDragOver ? ' upload-zone--active' : ''}${hasBatch ? ' upload-zone--compact' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          aria-label="Drop SD card folder here to upload"
        >
          {!hasBatch ? (
            <>
              <div className="upload-zone-icon">{isDragOver ? '📂' : '💾'}</div>
              <h2 className="upload-zone-title">
                {isDragOver ? 'Release to start upload' : 'Drop SD card folder here'}
              </h2>
              <p className="upload-zone-sub">JPEG · PNG · WebP · max 50 MB per file</p>
              {!warehouseId && (
                <p className="upload-zone-warn">⚠ No warehouse assigned — contact an admin</p>
              )}
              {missions.length > 0 && !activeMission && (
                <p className="upload-zone-warn">⚠ Select a mission above before uploading</p>
              )}
              {activeMission && (activeMission.allocated_aisles.length > 0) && !selectedAisle && (
                <p className="upload-zone-warn">⚠ Select an aisle above before uploading</p>
              )}
            </>
          ) : (
            <div className="upload-ledger">
              <div className="ledger-summary">
                <span className="ledger-fraction">
                  <span className="ledger-done">{doneCount}</span>
                  <span className="ledger-sep">/</span>
                  <span className="ledger-total">{totalCount}</span>
                </span>
                <span className="ledger-label">files uploaded</span>
                {failedCount > 0 && (
                  <span className="ledger-failed">· {failedCount} failed</span>
                )}
                {!isRunning && pendingCount === 0 && (
                  <button className="btn btn--ghost btn--sm ledger-reset" onClick={reset}>
                    New batch
                  </button>
                )}
              </div>

              <Progress
                value={totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0}
                variant={failedCount > 0 ? 'warning' : doneCount === totalCount ? 'success' : 'default'}
                className="my-1"
              />

              {currentItem ? (
                <div className="ledger-current">
                  <span className="ledger-spinner">⟳</span>
                  <span className="ledger-filename" title={currentItem.filename}>
                    {currentItem.filename}
                  </span>
                  <span className="ledger-filesize">{fmtBytes(currentItem.sizeBytes)}</span>
                </div>
              ) : isRunning ? (
                <div className="ledger-current ledger-current--idle">Preparing…</div>
              ) : pendingCount === 0 ? (
                <div className="ledger-current ledger-current--done">
                  <span>✓ Batch complete — {doneCount} photos uploaded</span>
                  {selectedAisle && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      style={{ marginLeft: '0.75rem' }}
                      onClick={() =>
                        navigate(
                          `/dashboard/triage/${missionId}?aisle=${encodeURIComponent(selectedAisle)}`,
                        )
                      }
                    >
                      Review Aisle {selectedAisle} Triage →
                    </button>
                  )}
                </div>
              ) : (
                <div className="ledger-current ledger-current--idle">
                  {isDragOver ? 'Release to add more files' : 'Drop more files to continue…'}
                </div>
              )}

              {failedCount > 0 && (
                <div className="ledger-errors">
                  {items
                    .filter(i => i.status === 'failed')
                    .slice(0, 5)
                    .map(i => (
                      <div key={i.id} className="ledger-error-row" title={i.error}>
                        ✗ {i.filename}
                        {i.error && <span className="ledger-error-msg"> — {i.error}</span>}
                      </div>
                    ))}
                  {failedCount > 5 && (
                    <div className="ledger-error-row">… and {failedCount - 5} more</div>
                  )}
                  {!isRunning && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      style={{ marginTop: '6px' }}
                      onClick={retryFailed}
                    >
                      ↺ Retry {failedCount} failed upload{failedCount !== 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Live scan feed ─────────────────────────────────────────────── */}
        <div className="pilot-event-feed">
          <h3 className="feed-title">Live Scan Feed</h3>
          {events.length === 0 ? (
            <div className="feed-empty">Waiting for scan results…</div>
          ) : (
            <div className="feed-list">
              {events.map(ev => (
                <div
                  key={ev.id}
                  className={`feed-item feed-item--${ev.event.toLowerCase()}${ev.out_of_scope ? ' feed-item--oos' : ''}`}
                >
                  <span className="feed-time">{ev.ts}</span>
                  <span className="feed-event">{ev.event}</span>
                  {ev.location_code && (
                    <span className="feed-location">{ev.location_code}</span>
                  )}
                  {ev.out_of_scope && (
                    <span className="feed-oos-badge">⚠ OUT OF SCOPE</span>
                  )}
                  {ev.canonical_filename && (
                    <span className="feed-filename">{ev.canonical_filename}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
